import React, { useEffect, useState } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

const MonthlyStatsWidget = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [monthOffset, setMonthOffset] = useState(0); // 0 = current month, -1 = last month, etc.
    const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
    const [chartWidth, setChartWidth] = useState(1000);
    const chartContainerRef = React.useRef(null);
    const Auth = useAuthentication();

    const getMonthDates = () => {
        const today = new Date();
        const targetDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);

        const firstDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        const lastDay = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);

        return { firstDay, lastDay };
    };

    useEffect(() => {
        loadStats();
    }, [monthOffset]);

    React.useEffect(() => {
        const updateWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [stats]);

    const loadStats = () => {
        setLoading(true);
        const { firstDay, lastDay } = getMonthDates();

        // Always start at day 1 of the month
        const startDate = firstDay.toISOString().split('T')[0];
        const endDate = lastDay.toISOString().split('T')[0];

        // Calculate actual number of days between start and end (inclusive)
        const daysInMonth = Math.round((lastDay - firstDay) / (1000 * 60 * 60 * 24)) + 1;

        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + `expenseEntries/allUsersDailyStats?days=${daysInMonth}&start_date=${startDate}`,
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setStats(response.data.data);
            }
            setLoading(false);
        }).catch(error => {
            console.error("Error loading monthly stats:", error);
            setLoading(false);
        });
    };

    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laden...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats || !stats.daily_data) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <p className="text-muted">Keine Daten verfügbar</p>
                </div>
            </div>
        );
    }

    // Calculate max hours for scaling
    const maxHours = Math.max(...stats.daily_data.map(day => day.hours), 1);
    const scaleMaxHours = Math.ceil(maxHours / 10) * 10; // Round up to nearest 10

    // Calculate total and average - keep precision for display
    const totalMinutes = stats.daily_data.reduce((sum, day) => sum + day.minutes, 0);
    const totalHours = totalMinutes / 60;
    const avgHours = totalHours / stats.daily_data.length;

    // Format function to show hours in HH:MM format
    const formatHours = (hours) => {
        const totalMins = Math.round(hours * 60);
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    // Get month name
    const { firstDay } = getMonthDates();
    const monthName = firstDay.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

    const chartHeight = 200;
    const padding = { top: 20, right: 20, bottom: 30, left: 50 };
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    // Calculate points for the line
    const points = stats.daily_data.map((day, index) => {
        const x = padding.left + (index / (stats.daily_data.length - 1)) * innerWidth;
        const y = padding.top + innerHeight - (day.hours / scaleMaxHours) * innerHeight;
        return { x, y, day, index };
    });

    // Create path data
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');

    // Create area path (fill under the line)
    const areaPath = `M ${padding.left},${padding.top + innerHeight} ` +
        points.map(p => `L ${p.x},${p.y}`).join(' ') +
        ` L ${padding.left + innerWidth},${padding.top + innerHeight} Z`;

    return (
        <>
            {/* Tooltip */}
            {tooltip.show && (
                <div
                    className="position-fixed text-white p-2 rounded-3 shadow-lg"
                    style={{
                        top: `${tooltip.y}px`,
                        left: `${tooltip.x}px`,
                        fontSize: '0.75rem',
                        zIndex: 99999,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        background: 'rgba(33, 37, 41, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transform: 'translate(-50%, -120%)'
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltip.content }}
                />
            )}

            <div className="card h-100" style={{
                boxShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.08)',
                backdropFilter: 'blur(10px) saturate(180%)',
                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.125)',
                background: 'rgba(15, 23, 42, 0.85)',
                color: '#fff'
            }}>
                <div className="card-body">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0 text-white">
                        <i className="bi bi-graph-up me-2" style={{ color: '#60a5fa' }}></i>
                        Team-Aufwände Monat
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-sm"
                            onClick={() => setMonthOffset(monthOffset - 1)}
                            style={{
                                background: 'rgba(96, 165, 250, 0.2)',
                                border: '1px solid rgba(96, 165, 250, 0.3)',
                                color: '#60a5fa'
                            }}
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="text-white small" style={{ minWidth: '150px', textAlign: 'center' }}>
                            {monthName}
                        </span>
                        <button
                            className="btn btn-sm"
                            onClick={() => setMonthOffset(monthOffset + 1)}
                            disabled={monthOffset >= 0}
                            style={{
                                background: monthOffset >= 0 ? 'rgba(100, 116, 139, 0.2)' : 'rgba(96, 165, 250, 0.2)',
                                border: monthOffset >= 0 ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(96, 165, 250, 0.3)',
                                color: monthOffset >= 0 ? '#64748b' : '#60a5fa',
                                cursor: monthOffset >= 0 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="row g-2 mb-4">
                    <div className="col-4">
                        <div className="text-center p-3 rounded" style={{
                            background: 'rgba(96, 165, 250, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(96, 165, 250, 0.2)'
                        }}>
                            <div className="h2 mb-0 fw-bold" style={{ color: '#93c5fd' }}>
                                {formatHours(totalHours)}
                            </div>
                            <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Gesamt</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="text-center p-3 rounded" style={{
                            background: 'rgba(52, 211, 153, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(52, 211, 153, 0.2)'
                        }}>
                            <div className="h2 mb-0 fw-bold" style={{ color: '#6ee7b7' }}>
                                {formatHours(avgHours)}
                            </div>
                            <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Ø pro Tag</small>
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="text-center p-3 rounded" style={{
                            background: 'rgba(251, 191, 36, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(251, 191, 36, 0.2)'
                        }}>
                            <div className="h2 mb-0 fw-bold" style={{ color: '#fcd34d' }}>
                                {formatHours(maxHours)}
                            </div>
                            <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Maximum</small>
                        </div>
                    </div>
                </div>

                {/* Line Chart */}
                <div className="mb-3">
                    <h6 className="mb-3 small" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Tägliche Buchungen
                    </h6>
                    <div ref={chartContainerRef} style={{ width: '100%' }}>
                        <svg
                            width="100%"
                            height={chartHeight}
                            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                            preserveAspectRatio="xMidYMid meet"
                            style={{ display: 'block' }}
                        >
                            {/* Grid lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((fraction, i) => {
                                const y = padding.top + innerHeight * (1 - fraction);
                                return (
                                    <g key={i}>
                                        <line
                                            x1={padding.left}
                                            y1={y}
                                            x2={padding.left + innerWidth}
                                            y2={y}
                                            stroke="rgba(255, 255, 255, 0.1)"
                                            strokeWidth="1"
                                        />
                                        <text
                                            x={padding.left - 10}
                                            y={y + 4}
                                            fill="rgba(255, 255, 255, 0.5)"
                                            fontSize="10"
                                            textAnchor="end"
                                        >
                                            {(scaleMaxHours * fraction).toFixed(0)}h
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Area under the line */}
                            <path
                                d={areaPath}
                                fill="url(#gradient)"
                                opacity="0.3"
                            />

                            {/* Gradient definition */}
                            <defs>
                                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
                                    <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.1" />
                                </linearGradient>
                            </defs>

                            {/* Line */}
                            <path
                                d={linePath}
                                fill="none"
                                stroke="#60a5fa"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />

                            {/* Points */}
                            {points.map((point, i) => (
                                <g key={i}>
                                    <circle
                                        cx={point.x}
                                        cy={point.y}
                                        r="4"
                                        fill="#60a5fa"
                                        stroke="#1e293b"
                                        strokeWidth="2"
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svgRect = e.currentTarget.closest('svg').getBoundingClientRect();
                                            setTooltip({
                                                show: true,
                                                content: `<div class="fw-bold">${new Date(point.day.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</div><div>${point.day.hours.toFixed(1)}h</div>`,
                                                x: rect.left + rect.width / 2,
                                                y: rect.top
                                            });
                                        }}
                                        onMouseLeave={() => {
                                            setTooltip({ show: false, content: '', x: 0, y: 0 });
                                        }}
                                    />
                                </g>
                            ))}

                            {/* X-axis labels - show every 5th day */}
                            {points.filter((_, i) => i % 5 === 0 || i === points.length - 1).map((point, i) => (
                                <text
                                    key={i}
                                    x={point.x}
                                    y={padding.top + innerHeight + 20}
                                    fill="rgba(255, 255, 255, 0.5)"
                                    fontSize="10"
                                    textAnchor="middle"
                                >
                                    {new Date(point.day.date).getDate()}.
                                </text>
                            ))}
                        </svg>
                    </div>
                </div>

                {/* Summary */}
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            <i className="bi bi-calendar-range me-1"></i>
                            {new Date(stats.summary.start_date).toLocaleDateString('de-DE')} bis {new Date(stats.summary.end_date).toLocaleDateString('de-DE')}
                        </small>
                        <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            <i className="bi bi-calendar3 me-1"></i>
                            {stats.summary.total_days} Tage
                        </small>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default MonthlyStatsWidget;
